import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, BookOpen, Star, AlertTriangle, MessageCircle, X } from "lucide-react";
import ModalSignalerProbleme from "./ModalSignalerProbleme";
import ModalNoterSite from "./ModalNoterSite";
import { useOverlayActions } from "./OverlayActionsContext";
import ModalReponseProbleme from "../ModalReponseProbleme/ModalReponseProbleme";


// Hook personnalisé pour détecter la taille de l'écran
function useMediaQuery(query: string) {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches);
        }
        const listener = () => setMatches(media.matches);
        media.addEventListener("change", listener);
        return () => media.removeEventListener("change", listener);
    }, [matches, query]);

    return matches;
}


export default function OverlayActions() {
    const [open, setOpen] = useState(false);
    const [openSignal, setOpenSignal] = useState(false);
    const [openNoter, setOpenNoter] = useState(false);
    const [openReponse, setOpenReponse] = useState(false);
    const [showBubble, setShowBubble] = useState(false);
    const router = useRouter();

    // Détection responsive
    const isSmallScreen = useMediaQuery("(max-width: 640px)");
    const isMediumScreen = useMediaQuery("(max-width: 1024px)");

    // Récupération des actions dynamiques
    const { actions } = useOverlayActions();

    // Vérifier si la bulle a déjà été fermée
    useEffect(() => {
        const bubbleDismissed = localStorage.getItem('overlayActionsBubbleDismissed');
        if (!bubbleDismissed) {
            const timer = setTimeout(() => {
                setShowBubble(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Fermer la bulle définitivement
    const handleCloseBubble = () => {
        setShowBubble(false);
        localStorage.setItem('overlayActionsBubbleDismissed', 'true');
    };

    const resetBubble = () => {
        localStorage.removeItem('overlayActionsBubbleDismissed');
        setShowBubble(true);
    };

    // Actions globales avec labels adaptatifs (SOLUTION 1)
    const globalActions = [
        {
            label: isSmallScreen ? "Aide" : "Aide",
            onClick: () => { router.push("/aide"); },
            icon: <BookOpen size={20} />,
            closeOnClick: true,
        },
        {
            label: isSmallScreen ? "Noter" : "Noter le site",
            onClick: () => setOpenNoter(true),
            icon: <Star size={20} />,
            closeOnClick: false,
        },
        {
            label: isSmallScreen ? "Signaler" : "Signaler un problème",
            onClick: () => setOpenSignal(true),
            icon: <AlertTriangle size={20} />,
            closeOnClick: false,
        },
        {
            label: isSmallScreen ? "Réponses" : "Réponses aux problèmes",
            onClick: () => setOpenReponse(true),
            icon: <MessageCircle size={20} />,
            closeOnClick: false,
        },
        {
            label: isSmallScreen ? "Bulle" : "Réafficher la bulle d'aide",
            onClick: resetBubble,
            icon: <span style={{ fontSize: "1.2rem" }}>💡</span>,
            closeOnClick: true,
        },
    ];

    const allActions = [...globalActions, ...actions];

    // Styles responsive pour le conteneur principal
    const containerStyle = {
        position: "fixed" as const,
        bottom: isSmallScreen ? 20 : 50,
        right: isSmallScreen ? 15 : 30,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "flex-end",
    };

    // Styles responsive pour les boutons d'action (SOLUTION 1)
    const actionButtonWidth = isSmallScreen ? "160px" : "260px";
    const actionFontSize = isSmallScreen ? "0.85rem" : "1rem";
    const actionPadding = isSmallScreen ? "8px 12px" : "10px 18px";
    const actionGap = isSmallScreen ? "8px" : "10px";
    const actionMarginBottom = isSmallScreen ? 8 : 10;

    // Styles responsive pour la bulle
    const bubbleStyle = {
        position: "absolute" as const,
        bottom: isSmallScreen ? 60 : 70,
        right: isSmallScreen ? 0 : -10,
        background: "linear-gradient(135deg, var(--secondary-theme), color-mix(in srgb, var(--secondary-theme) 80%, white))",
        color: "var(--background)",
        padding: isSmallScreen ? "12px 14px" : "14px 18px",
        borderRadius: isSmallScreen ? "12px" : "16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        width: isSmallScreen ? "240px" : isMediumScreen ? "260px" : "280px",
        marginBottom: "10px",
        whiteSpace: "normal" as const,
    };

    // Styles responsive pour le bouton principal
    const mainButtonSize = isSmallScreen ? 50 : 60;

    return (
        <>
            <ModalSignalerProbleme open={openSignal} onClose={() => setOpenSignal(false)} />
            <ModalNoterSite open={openNoter} onClose={() => setOpenNoter(false)} />
            <ModalReponseProbleme open={openReponse} onClose={() => setOpenReponse(false)} />

            <div style={containerStyle}>
                {/* Bulle d'information animée */}
                <AnimatePresence>
                    {showBubble && !open && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                y: 0,
                                transition: {
                                    type: "spring",
                                    bounce: 0.4,
                                    duration: 0.6
                                }
                            }}
                            exit={{
                                opacity: 0,
                                scale: 0.8,
                                y: 20,
                                transition: { duration: 0.2 }
                            }}
                            style={bubbleStyle}
                        >
                            {/* Bouton fermer */}
                            <button
                                onClick={handleCloseBubble}
                                style={{
                                    position: "absolute",
                                    top: "6px",
                                    right: "6px",
                                    background: "rgba(255,255,255,0.2)",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: isSmallScreen ? "20px" : "24px",
                                    height: isSmallScreen ? "20px" : "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.3)";
                                    e.currentTarget.style.transform = "scale(1.1)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                                    e.currentTarget.style.transform = "scale(1)";
                                }}
                                aria-label="Fermer"
                            >
                                <X size={isSmallScreen ? 12 : 14} />
                            </button>

                            {/* Contenu de la bulle */}
                            <div style={{ paddingRight: isSmallScreen ? "18px" : "20px" }}>
                                <h4 style={{
                                    margin: "0 0 6px 0",
                                    fontSize: isSmallScreen ? "0.9rem" : "1rem",
                                    fontWeight: "600"
                                }}>
                                    💡 Besoin d'aide ?
                                </h4>
                                <p style={{
                                    margin: 0,
                                    fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                                    lineHeight: "1.4",
                                    opacity: 0.95,
                                    color: "var(--background)",
                                }}>
                                    {isSmallScreen
                                        ? "Cliquez ici pour les actions rapides !"
                                        : "Cliquez ici pour accéder aux actions rapides et obtenir de l'aide !"}
                                </p>
                            </div>

                            {/* Flèche pointant vers le bouton */}
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: "-8px",
                                    right: isSmallScreen ? "15px" : "20px",
                                    width: "0",
                                    height: "0",
                                    borderLeft: "10px solid transparent",
                                    borderRight: "10px solid transparent",
                                    borderTop: "10px solid color-mix(in srgb, var(--secondary-theme) 80%, white)",
                                }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Boutons animés (SOLUTION 1 - Labels adaptatifs) */}
                <AnimatePresence>
                    {open &&
                        allActions.map((action, i) => (
                            <motion.button
                                key={action.label}
                                initial={{ opacity: 0, x: 40, scale: 0.92 }}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                    scale: 1,
                                    transition: { type: "spring", delay: i * 0.07 },
                                }}
                                exit={{
                                    opacity: 0,
                                    x: 40,
                                    scale: 0.92,
                                    transition: { duration: 0.18 },
                                }}
                                onClick={() => {
                                    if (action.closeOnClick) {
                                        setOpen(false);
                                    }
                                    action.onClick();
                                }}
                                style={{
                                    marginBottom: actionMarginBottom,
                                    background: "var(--secondary-theme)",
                                    color: "var(--background)",
                                    border: "1px solid var(--seeric-border)",
                                    borderRadius: isSmallScreen ? "10px" : "12px",
                                    padding: actionPadding,
                                    fontSize: actionFontSize,
                                    width: actionButtonWidth,
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.14)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: actionGap,
                                }}
                                whileHover={{ scale: 1.06, y: -2 }}
                            >
                                <span style={{ display: "flex", flexShrink: 0 }}>
                                    {action.icon}
                                </span>
                                <span style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>
                                    {action.label}
                                </span>
                            </motion.button>
                        ))}
                </AnimatePresence>

                {/* Bouton principal */}
                <motion.button
                    aria-label="Actions rapides"
                    onClick={() => {
                        setOpen(o => !o);
                        if (showBubble) {
                            handleCloseBubble();
                        }
                    }}
                    animate={open ? {
                        rotate: 180,
                        scale: 1,
                        opacity: 1
                    } : {
                        rotate: 0,
                        scale: 1,
                        opacity: 1
                    }}
                    style={{
                        background: "color-mix(in srgb, var(--secondary-theme), transparent 40%)",
                        color: "var(--background)",
                        border: "none",
                        borderRadius: "50%",
                        width: mainButtonSize,
                        height: mainButtonSize,
                        fontSize: isSmallScreen ? "1.5rem" : "1.9rem",
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.21)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    whileTap={{ scale: 0.94 }}
                    data-tut="overlayActions"
                >
                    <motion.div
                        animate={!open ? {
                            rotate: [-15, 15, -15],
                            scale: [1, 1.08, 1],
                        } : {
                            rotate: 0,
                            scale: 1
                        }}
                        transition={!open ? {
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                        } : {
                            duration: 0.3
                        }}
                    >
                        <ChevronLeft size={isSmallScreen ? 28 : 32} />
                    </motion.div>
                </motion.button>
            </div>
        </>
    );
}
